// pages/SignUp.tsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Container,
    IconButton,
    InputAdornment,
    TextField,
    Typography
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
//import FacebookIcon from "@mui/icons-material/Facebook";

declare global {
    interface Window {
        fbAsyncInit: () => void;
        FB: any;
    }
}

const SignUp: React.FC = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', dob: '' });

    // Load Facebook SDK
    useEffect(() => {
        window.fbAsyncInit = function () {
            window.FB.init({
                appId: process.env.REACT_APP_FACEBOOK_APP_ID,
                cookie: true,
                xfbml: true,
                version: 'v21.0',
            });
        };

        (function (d, s, id) {
            const fjs = d.getElementsByTagName(s)[0];
            if (d.getElementById(id)) { return; }
            const js = d.createElement(s) as HTMLScriptElement;
            js.id = id;
            js.src = "https://connect.facebook.net/en_US/sdk.js";
            fjs.parentNode?.insertBefore(js, fjs);
        }(document, 'script', 'facebook-jssdk'));
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (form.password !== form.confirmPassword) {
            alert("Passwords don't match");
            return;
        }
        console.log('Sign up form submitted', form);
        navigate('/');
    };

    const handleGoogleSuccess = async (credentialResponse: any) => {
        try {
            const res = await fetch('/auth/google', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: credentialResponse.credential }),
            });
            const data = await res.json();
            console.log('Google login success:', data);
            navigate('/');
        } catch (error) {
            console.error('Google login error:', error);
        }
    };

    const handleFacebookLogin = () => {
        window.FB.login(
            (response: any) => {
                if (response.authResponse) {
                    console.log('Facebook login success:', response);
                    fetch('/auth/facebook', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ accessToken: response.authResponse.accessToken }),
                    })
                        .then(res => res.json())
                        .then(data => {
                            console.log('Facebook backend response:', data);
                            navigate('/');
                        })
                        .catch(err => console.error('Facebook login error:', err));
                } else {
                    console.log('User cancelled Facebook login');
                }
            },
            { scope: 'public_profile,email' }
        );
    };

    return (
        <Container
            maxWidth="sm"
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Box sx={{ p: 4, width: '100%' }}>
                <Typography variant="h5" align="center" fontWeight="bold" mb={2}>
                    Create Account
                </Typography>

                <Box display="flex" gap={2} justifyContent="center" mb={2}>
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<FacebookIcon />}
                        sx={{ textTransform: 'none' }}
                        onClick={handleFacebookLogin}
                    >
                        Facebook
                    </Button>

                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => console.log('Google Login Failed')}
                        useOneTap
                        text="continue_with"
                    />
                </Box>

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Date of Birth"
                        name="dob"
                        type="date"
                        value={form.dob}
                        onChange={handleChange}
                        margin="normal"
                        required
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={handleChange}
                        margin="normal"
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Confirm Password"
                        name="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={form.confirmPassword}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />

                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        sx={{ mt: 3, borderRadius: 3 }}
                    >
                        Sign Up
                    </Button>
                </form>

                <Typography align="center" sx={{ mt: 2 }}>
                    Already have an account?{' '}
                    <Button variant="text" onClick={() => navigate('/')} sx={{ textTransform: 'none' }}>
                        Sign In
                    </Button>
                </Typography>
            </Box>
        </Container>
    );
};

export default SignUp;
